import React, { useEffect, useMemo, useRef, useState } from 'react';
import classname from 'classnames';
import {
    ICmsNavItemsData,
    ICusNewNavMenuProps,
    IImageLinksData,
    IPromotedLinksData,
    ISubMenusData
} from './cus-new-nav-menu.props.autogenerated';
import { ISubMenusDataWithGrouping } from './cus-new-nav-menu.data';
import { Gender } from '../../shared/services/gender-cookie-service/gender-cookie-service.types';
import { CookieNames } from '../../shared/services/cookie-service/cookie-service.const';
import { CookieService } from '../../shared/services/cookie-service/cookie-service';
import { useIsMobile } from '../../shared/hooks/useIsMobile';
import { useIsClientSide } from '../../utils/hooks';
import { IGridSettings, Image } from '@msdyn365-commerce/core-internal';
import { BaseImageSettings, useImageSettings } from '../../shared/hooks/useImageSettings';

const BASE_IMAGE_VIEWPORT_SETTINGS: BaseImageSettings = {
    xs: 'navMenuMedium',
    sm: 'navMenuLarge',
    md: 'navMenuSmall',
    lg: 'navMenuSmall',
    xl: 'navMenuSmall'
};

const getLinkTarget = (openInNewTab: boolean | undefined) => {
    return openInNewTab ? '_blank' : '_self';
};

const MenuItems = ({ items, onDrill }: { items: ISubMenusDataWithGrouping[]; onDrill: (item: ISubMenusDataWithGrouping) => void }) => {
    return (
        <ul className='new-nav-menu__menu-items-container'>
            {items.map(item => {
                const hasChildren = !!item.subMenus?.length;
                return (
                    <li
                        key={item.labelText}
                        className={classname('new-nav-menu__menu-item', {
                            'new-nav-menu__menu-item--last-in-group': item.isLastInGroup,
                            'new-nav-menu__menu-item--highlighted': item.isHighlighted
                        })}
                    >
                        {hasChildren && (
                            <button type='button' onClick={() => onDrill(item)} aria-label={item.ariaLabel || item.labelText}>
                                {item.labelText}
                            </button>
                        )}
                        {!hasChildren && item.linkUrl && (
                            <a href={item.linkUrl.destinationUrl} aria-label={item.ariaLabel || item.labelText}>
                                {item.labelText}
                            </a>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

const RootLevelLinks = ({ items, type }: { items: IPromotedLinksData[] | undefined; type: 'promotional' | 'informational' }) => {
    if (!items || items.length === 0) {
        return <></>;
    }
    return (
        <ul
            className={classname('new-nav-menu__root-level-links-container', {
                'new-nav-menu__root-level-links-container--position-top': type === 'promotional',
                'new-nav-menu__root-level-links-container--position-bottom': type === 'informational'
            })}
        >
            {items.map(item => (
                <li key={item.labelText}>
                    <a
                        className={classname('new-nav-menu__root-level-link', {
                            'new-nav-menu__root-level-link--large': type === 'promotional'
                        })}
                        href={item.linkUrl.destinationUrl}
                        target={getLinkTarget(item.openInNewTab)}
                        aria-label={item.ariaLabel || item.labelText}
                    >
                        {item.labelText}
                    </a>
                </li>
            ))}
        </ul>
    );
};

const ImageLinks = ({
    imageLinks,
    gridSettings
}: {
    imageLinks: IImageLinksData[] | undefined;
    gridSettings: IGridSettings | undefined;
}) => {
    const imageViewportSettings = useImageSettings(BASE_IMAGE_VIEWPORT_SETTINGS);

    if (!imageLinks || imageLinks.length === 0) {
        return <></>;
    }

    return (
        <div className='new-nav-menu__image-links-container'>
            {imageLinks.map(imageLink => (
                <a
                    key={imageLink.labelTitle}
                    href={imageLink.linkUrl.destinationUrl}
                    target={getLinkTarget(imageLink.openInNewTab)}
                    className='new-nav-menu__image-link'
                    aria-label={imageLink.ariaLabel || imageLink.labelTitle}
                >
                    <Image
                        className='new-nav-menu__image'
                        src={imageLink.image?.src}
                        alt={imageLink.image?.altText}
                        title={imageLink.image?.title}
                        imageSettings={{ ...imageLink.image?.imageSettings, viewports: imageViewportSettings }}
                        gridSettings={gridSettings ?? {}}
                    />
                    {imageLink.labelTitle && (
                        <span className='new-nav-menu__image-link-label'>
                            {imageLink.labelTitle}
                            {imageLink.labelSubtitle && (
                                <span className='new-nav-menu__image-link-label-subtitle'>{imageLink.labelSubtitle}</span>
                            )}
                        </span>
                    )}
                </a>
            ))}
        </div>
    );
};

const NavigationPanel = ({
    item,
    isMobile = false,
    stack,
    gridSettings,
    handleBackButtonClick,
    handleMenuItemClick
}: {
    item: ISubMenusData | ICmsNavItemsData;
    isMobile?: boolean;
    stack: ISubMenusData[];
    gridSettings: IGridSettings | undefined;
    handleBackButtonClick: () => void;
    handleMenuItemClick: (item: ISubMenusDataWithGrouping) => void;
}) => {
    const currentItems = useMemo(() => {
        if (stack.length) {
            return stack[stack.length - 1].subMenus ?? [];
        }
        if ('subMenuGroups' in item && item.subMenuGroups) {
            return item.subMenuGroups.flatMap(group =>
                group.subMenus.map((subMenu, i) => {
                    return { ...subMenu, isLastInGroup: i === group.subMenus.length - 1 };
                })
            );
        }
        if ('subMenus' in item) {
            return item.subMenus ?? [];
        }
        return [];
    }, [stack, item]);

    const title = stack.length ? stack[stack.length - 1].labelText : undefined;
    const imageLinks = stack.length ? stack[stack.length - 1].imageLinks : item.imageLinks;
    const shouldRenderHeader = title || stack.length > 0;

    return (
        <>
            <div className={classname('new-nav-menu__panel-main-container')}>
                {shouldRenderHeader && (
                    <div className='new-nav-menu__panel-header'>
                        {stack.length > 0 && (
                            <button type='button' className='new-nav-menu__back' onClick={handleBackButtonClick}>
                                ←
                            </button>
                        )}
                        {title && <div className='new-nav-menu__title'>{title}</div>}
                    </div>
                )}
                {!stack.length && 'promotedLinks' in item && <RootLevelLinks items={item.promotedLinks} type='promotional' />}
                <MenuItems items={currentItems} onDrill={item => handleMenuItemClick(item)} />
                {isMobile && <ImageLinks imageLinks={imageLinks} gridSettings={gridSettings} />}
                {'informationalLinks' in item && <RootLevelLinks items={item.informationalLinks} type='informational' />}
            </div>
            {!isMobile && <ImageLinks imageLinks={imageLinks} gridSettings={gridSettings} />}
        </>
    );
};

const NewNavMenu = (props: ICusNewNavMenuProps<{}>) => {
    const { config, context, id, resources } = props;
    const { cmsNavItems } = config;

    const isMobile = useIsMobile(context);
    const isClientSide = useIsClientSide();

    // Selection (DAME/HERRE preference) is separate from visibility (open panel).
    const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
    const [openCategoryIndex, setOpenCategoryIndex] = useState<number | null>(null);
    const [stack, setStack] = useState<ISubMenusData[]>([]);
    const categoryTriggerRefs = useRef<(HTMLElement | null)[]>([]);

    useEffect(() => {
        if (!cmsNavItems?.length) {
            return;
        }
        const genderCookieValue = CookieService.getRequestCookie<Gender>(CookieNames.Gender, context.request);
        if (genderCookieValue === 'male') {
            const defaultMaleCategoryIndex = cmsNavItems.findIndex(({ isDefaultMaleCategory }) => !!isDefaultMaleCategory);
            if (defaultMaleCategoryIndex !== -1) {
                setSelectedCategoryIndex(defaultMaleCategoryIndex);
            }
        }
    }, [cmsNavItems, context.request]);

    if (!cmsNavItems?.length) {
        return <></>;
    }

    const panelId = `${id}-nav-panel`;
    const ariaControlsId = !isMobile ? panelId : undefined;

    const hasPanelContent = (item: ICmsNavItemsData | undefined) => !!item?.subMenuGroups?.length;

    const openCategory = (index: number) => {
        setOpenCategoryIndex(hasPanelContent(cmsNavItems[index]) ? index : null);
        setStack([]);
    };

    const closePanel = () => {
        setOpenCategoryIndex(null);
        setStack([]);
    };

    const handleDesktopIntent = (index: number) => () => {
        if (isMobile || index === openCategoryIndex) {
            return;
        }
        openCategory(index);
    };

    const handleMouseLeave = () => {
        if (isMobile) {
            return;
        }
        closePanel();
    };

    const handleMobileTap = (index: number) => () => {
        if (openCategoryIndex === index) {
            return;
        }
        openCategory(index);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        // On mobile the drawer modal owns Escape handling.
        if (event.key !== 'Escape' || openCategoryIndex === null || isMobile) {
            return;
        }
        const trigger = categoryTriggerRefs.current[openCategoryIndex];
        closePanel();
        trigger?.focus();
    };

    const handleBackButtonClick = () => {
        setStack(s => s.slice(0, -1));
    };

    const handleMenuItemClick = (item: ISubMenusDataWithGrouping) => {
        setStack(s => [...s, item]);
    };

    const isPanelOpen = openCategoryIndex !== null;
    // The mobile drawer keeps a panel permanently visible, defaulting to the selected category.
    const visibleCategoryIndex = openCategoryIndex ?? (isMobile ? selectedCategoryIndex : null);
    const visibleCategory = visibleCategoryIndex !== null ? cmsNavItems[visibleCategoryIndex] : undefined;
    const highlightedCategoryIndex = isMobile ? (openCategoryIndex ?? selectedCategoryIndex) : openCategoryIndex;

    const renderNavigationPanel = () => {
        if (!visibleCategory || !isClientSide) {
            return <></>;
        }
        return (
            <NavigationPanel
                item={visibleCategory}
                stack={stack}
                gridSettings={context.request.gridSettings}
                handleBackButtonClick={handleBackButtonClick}
                handleMenuItemClick={handleMenuItemClick}
                isMobile={isMobile}
            />
        );
    };

    return (
        <>
            <nav className='new-nav-menu' aria-label={resources.menuAriaLabel} onMouseLeave={handleMouseLeave} onKeyDown={handleKeyDown}>
                <ul className='new-nav-menu__main-categories'>
                    {cmsNavItems.map((item, index) => {
                        const controlsPanel = hasPanelContent(item);
                        const commonProps = {
                            className: classname('new-nav-menu__category-button', {
                                'new-nav-menu__category-button--selected': highlightedCategoryIndex === index
                            }),
                            'aria-label': item.ariaLabel || item.labelText,
                            'aria-expanded': controlsPanel ? openCategoryIndex === index : undefined,
                            'aria-controls': controlsPanel ? ariaControlsId : undefined
                        };
                        const setTriggerRef = (element: HTMLElement | null) => {
                            categoryTriggerRefs.current[index] = element;
                        };
                        return (
                            <li key={`${item.labelText}-${index}`} onMouseEnter={handleDesktopIntent(index)}>
                                {isMobile && controlsPanel ? (
                                    <button type='button' ref={setTriggerRef} onClick={handleMobileTap(index)} {...commonProps}>
                                        {item.labelText}
                                    </button>
                                ) : (
                                    <a
                                        href={item.linkUrl.destinationUrl}
                                        ref={setTriggerRef}
                                        onFocus={handleDesktopIntent(index)}
                                        {...commonProps}
                                    >
                                        {item.labelText}
                                    </a>
                                )}
                            </li>
                        );
                    })}
                </ul>
                {!isMobile && (
                    <div
                        id={panelId}
                        aria-hidden={!isPanelOpen}
                        className={classname('new-nav-menu__panel', {
                            'new-nav-menu__panel--visible': isPanelOpen
                        })}
                    >
                        {renderNavigationPanel()}
                    </div>
                )}
                {isMobile && renderNavigationPanel()}
            </nav>
            {!isMobile && (
                <div
                    aria-hidden={!isPanelOpen}
                    className={classname('new-nav-menu-overlay', {
                        'new-nav-menu-overlay--visible': isPanelOpen
                    })}
                ></div>
            )}
        </>
    );
};

export default NewNavMenu;
